/**
 * Kanonischer Request-Vertrag für die `icarus-chat` Edge Function.
 *
 * Warum dieses Modul existiert
 * ────────────────────────────
 * Es gab ZWEI Frontend-Aufrufer mit ZWEI unvereinbaren Body-Shapes
 * gegen genau eine Function:
 *
 *   1. Cockpit-Chat (`useNutriChat` → `callIcarusChat`)
 *      → `{ message, history: [...inkl. neuer Nachricht] }`
 *   2. Globaler Dädalus-Assistent (`useIcarusChat`)
 *      → `{ messages: [...], context: {...}, stream: true }`
 *
 * `icarus-chat/index.ts` las ausschließlich Shape 1. Shape 2 endete
 * damit in jedem einzelnen Fall in `400 – Nachricht fehlt`: der
 * Assistent, der auf jeder Seite der App als Button hängt und per
 * Strg/Cmd+J geöffnet wird, hat nie eine Antwort geliefert. Kein Test
 * deckte den Pfad ab, weil die Route-Audits (`FEATURE_STATUS.md`)
 * Layout-Komponenten nicht erfassen.
 *
 * Dieses Modul ist die eine Stelle, die eingehende Bodys auf eine
 * kanonische Form bringt — und die eine Stelle, an der das Frontend
 * seinen Body baut. Ein Shape, beide Seiten.
 *
 * Deno-Mirror
 * ───────────
 * Die Edge Function kann nicht aus `src/` importieren. `pnpm interop:sync`
 * spiegelt diese Datei nach `supabase/functions/icarus-chat/request.ts`;
 * die Drift-CI (`pnpm interop:check`) bricht den Build, wenn Mirror und
 * Quelle auseinanderlaufen. Deshalb hat dieses Modul **keine Imports**
 * und ist frei von React, Supabase und `@/`-Aliassen.
 */

// ── Wire-Typen ───────────────────────────────────────────────────────────────

/** Eine Konversationsrunde, wie sie an das Modell geht. */
export interface TurnMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Kontext der aufrufenden Oberfläche. Enthält bewusst KEINE
 * Patientendaten — nur die (serverseitig noch zu verifizierende) ID und
 * den aktuellen App-Pfad. PHI gelangt ausschließlich über die
 * consent-gegateten Tools in den LLM-Kontext (siehe `tools.ts`).
 */
export interface ChatContext {
  patientId: string | null;
  currentPath: string | null;
}

/** Ein bestätigter Schritt im Plan-Modus. */
export interface PlanStepPayload {
  tool: string;
  args: Record<string, unknown>;
  description: string;
}

/** Body von `execute_plan`-Requests nach Bestätigung durch die Fachperson. */
export interface ExecutePlanPayload {
  steps: PlanStepPayload[];
}

/** Roher, ungeprüfter JSON-Body. Jedes Feld ist `unknown`. */
export interface RawChatBody {
  message?: unknown;
  history?: unknown;
  messages?: unknown;
  context?: unknown;
  assistant_name?: unknown;
  plan_only?: unknown;
  execute_plan?: unknown;
}

export interface NormalizedChatRequest {
  /** Die neue Nutzernachricht dieses Turns. Leer bei `execute_plan`. */
  message: string;
  /** Bisheriger Verlauf OHNE die neue Nutzernachricht. */
  history: TurnMessage[];
  context: ChatContext;
  assistantName: string | null;
  planOnly: boolean;
  executePlan: ExecutePlanPayload | null;
}

// ── Limits ───────────────────────────────────────────────────────────────────

/** Verlaufslänge, die an das Modell geht. Deckelt Tokenkosten und Latenz. */
export const MAX_HISTORY_TURNS = 40;
/** Zeichenlimit pro Nachricht. Schützt vor Prompt-Flooding. */
export const MAX_CONTENT_CHARS = 8_000;
/** Zeichenlimit für den durchgereichten App-Pfad. */
export const MAX_PATH_CHARS = 200;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Interne App-Pfade. Kein Schema, kein Host, keine protokollrelative URL —
 * der Pfad landet im Systemprompt, also gilt dieselbe Strenge wie beim
 * `navigate_to`-Tool.
 */
const SAFE_PATH_RE = /^\/[A-Za-z0-9/_.-]*$/;

function clampContent(value: string): string {
  return value.length > MAX_CONTENT_CHARS
    ? value.slice(0, MAX_CONTENT_CHARS)
    : value;
}

// ── Normalisierung (serverseitig) ────────────────────────────────────────────

/**
 * Filtert eine rohe Nachrichtenliste auf wohlgeformte User-/Assistant-
 * Runden. `system`- und `tool`-Rollen werden verworfen: den Systemprompt
 * baut die Function selbst, Tool-Ergebnisse entstehen serverseitig im
 * Loop. Ein Client darf beides nicht setzen können.
 */
export function coerceTurns(raw: unknown): TurnMessage[] {
  if (!Array.isArray(raw)) return [];

  const turns: TurnMessage[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const { role, content } = entry as { role?: unknown; content?: unknown };
    if (role !== 'user' && role !== 'assistant') continue;
    if (typeof content !== 'string') continue;
    const trimmed = content.trim();
    if (!trimmed) continue;
    turns.push({ role, content: clampContent(trimmed) });
  }

  return turns.length > MAX_HISTORY_TURNS
    ? turns.slice(turns.length - MAX_HISTORY_TURNS)
    : turns;
}

/**
 * Trennt die neue Nutzernachricht vom bisherigen Verlauf.
 *
 * Beide Aufrufer hängen die neue Nachricht ans Ende ihres Verlaufs an —
 * der Cockpit-Chat zusätzlich zum separaten `message`-Feld, der globale
 * Assistent ausschließlich im Array. Statt wie bisher blind das letzte
 * Element zu verwerfen (`history.slice(0, -1)`), wird es nur dann
 * entfernt, wenn es tatsächlich die neue Nachricht ist. Damit verliert
 * ein Aufrufer, der `history` ohne Duplikat schickt, keine echte Runde.
 */
function splitTurns(raw: RawChatBody): { message: string; history: TurnMessage[] } {
  const explicit =
    typeof raw.message === 'string' ? clampContent(raw.message.trim()) : '';

  if (explicit) {
    const history = coerceTurns(raw.history);
    const last = history[history.length - 1];
    const isDuplicate = last?.role === 'user' && last.content === explicit;
    return {
      message: explicit,
      history: isDuplicate ? history.slice(0, -1) : history,
    };
  }

  const turns = coerceTurns(raw.messages);
  const last = turns[turns.length - 1];
  if (last?.role === 'user') {
    return { message: last.content, history: turns.slice(0, -1) };
  }

  // Kein `message`, kein abschließender User-Turn: der Aufrufer hat nichts
  // zu sagen. `index.ts` beantwortet das mit 400, sofern kein
  // `execute_plan` vorliegt.
  return { message: '', history: turns };
}

/** Liest `context` defensiv. Unbekannte/ungültige Werte werden zu `null`. */
export function normalizeContext(raw: unknown): ChatContext {
  if (!raw || typeof raw !== 'object') {
    return { patientId: null, currentPath: null };
  }
  const { patient_id, current_path } = raw as {
    patient_id?: unknown;
    current_path?: unknown;
  };

  const patientId =
    typeof patient_id === 'string' && UUID_RE.test(patient_id.trim())
      ? patient_id.trim()
      : null;

  const rawPath = typeof current_path === 'string' ? current_path.trim() : '';
  const currentPath =
    rawPath.length > 0 &&
    rawPath.length <= MAX_PATH_CHARS &&
    SAFE_PATH_RE.test(rawPath) &&
    !rawPath.includes('//')
      ? rawPath
      : null;

  return { patientId, currentPath };
}

/** Liest `execute_plan`; `null`, wenn kein ausführbarer Plan anliegt. */
function normalizeExecutePlan(raw: unknown): ExecutePlanPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const { steps } = raw as { steps?: unknown };
  if (!Array.isArray(steps) || steps.length === 0) return null;

  const valid: PlanStepPayload[] = [];
  for (const step of steps) {
    if (!step || typeof step !== 'object') continue;
    const { tool, args, description } = step as {
      tool?: unknown;
      args?: unknown;
      description?: unknown;
    };
    if (typeof tool !== 'string' || !tool.trim()) continue;
    valid.push({
      tool: tool.trim(),
      args:
        args && typeof args === 'object' && !Array.isArray(args)
          ? (args as Record<string, unknown>)
          : {},
      description:
        typeof description === 'string' && description.trim()
          ? description.trim()
          : tool.trim(),
    });
  }
  if (valid.length === 0) return null;

  return { steps: valid };
}

/**
 * Bringt jeden unterstützten Body-Shape auf die kanonische Form.
 * Wirft nie — ein unbrauchbarer Body ergibt eine leere Nachricht, die
 * der Aufrufer mit 400 beantwortet.
 */
export function normalizeChatRequest(raw: RawChatBody): NormalizedChatRequest {
  const { message, history } = splitTurns(raw);

  return {
    message,
    history,
    context: normalizeContext(raw.context),
    assistantName:
      typeof raw.assistant_name === 'string' && raw.assistant_name.trim()
        ? raw.assistant_name.trim().slice(0, 60)
        : null,
    planOnly: raw.plan_only === true,
    executePlan: normalizeExecutePlan(raw.execute_plan),
  };
}

/**
 * Baut den Kontext-Abschnitt des Systemprompts.
 *
 * DSGVO-Hinweis: hier steht bewusst nur die Patienten-ID, nie Name oder
 * andere Art-9-Daten. Die ID allein erlaubt dem Modell, die richtigen
 * Tools ohne Rückfrage aufzurufen; die Tools selbst prüfen die
 * KI-Einwilligung (Art. 22 DSGVO) und die Mandantenzugehörigkeit.
 *
 * `patientId` wird nur übergeben, wenn der Aufrufer sie zuvor gegen die
 * Organisation verifiziert hat (siehe `index.ts`).
 */
export function buildContextSection(ctx: ChatContext): string {
  const lines: string[] = [];

  if (ctx.currentPath) {
    lines.push(`Die Fachperson befindet sich gerade auf der Seite ${ctx.currentPath}.`);
  }
  if (ctx.patientId) {
    lines.push(
      `Sie hat die Akte des Patienten mit der ID ${ctx.patientId} geöffnet.`,
      'Bezieht sich eine Anfrage auf "diesen Patienten", "ihn", "sie" oder ' +
        '"die Akte", verwende diese ID direkt — frage nicht nach dem Namen.',
      'Patientendaten rufst du ausschließlich über die Tools ab; ' +
        'die Einwilligungsprüfung passiert dort.',
    );
  }

  if (lines.length === 0) return '';
  return `\n\nAKTIVER KONTEXT:\n${lines.join('\n')}`;
}

// ── Body-Bau (clientseitig) ──────────────────────────────────────────────────

export interface BuildChatBodyInput {
  /** Neue Nutzernachricht. Leer lassen, wenn `executePlan` gesetzt ist. */
  message?: string;
  /** Bisheriger Verlauf; die neue Nachricht darf enthalten sein oder nicht. */
  history?: TurnMessage[];
  context?: Partial<ChatContext>;
  assistantName?: string | null;
  planOnly?: boolean;
  executePlan?: ExecutePlanPayload | null;
}

/**
 * Baut den Request-Body in der kanonischen Form.
 *
 * Jeder Frontend-Aufrufer geht hierdurch — damit kann kein zweiter
 * Shape mehr entstehen, ohne dass diese Datei (und ihre Tests) es
 * mitbekommen.
 */
export function buildChatRequestBody(input: BuildChatBodyInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    message: input.message?.trim() ?? '',
    history: coerceTurns(input.history ?? []),
  };

  const context = normalizeContext({
    patient_id: input.context?.patientId ?? undefined,
    current_path: input.context?.currentPath ?? undefined,
  });
  if (context.patientId || context.currentPath) {
    body.context = {
      patient_id: context.patientId ?? undefined,
      current_path: context.currentPath ?? undefined,
    };
  }

  if (input.assistantName) body.assistant_name = input.assistantName;
  if (input.planOnly) body.plan_only = true;
  if (input.executePlan) body.execute_plan = input.executePlan;

  return body;
}
